import { useEffect, useMemo, useRef, useState } from "react";

const INITIAL_FORM = {
  employeeId: "",
  type: "annual",
  status: "pending",
  startDate: "",
  endDate: "",
  notes: "",
};

const normalizeDateInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

function LeaveDrawer({
  open,
  onClose,
  onSubmit,
  saving = false,
  error = "",
  employees = [],
  leave = null,
  isAdmin = false,
  lockedEmployeeId = "",
}) {
  const drawerRef = useRef(null);
  const isEditMode = Boolean(leave?.id);
  const isReadOnlyStatus = !isAdmin && isEditMode && leave?.status && leave.status !== "pending";
  const [form, setForm] = useState(INITIAL_FORM);
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (!drawerRef.current) return;
      if (drawerRef.current.contains(event.target)) return;
      onClose?.();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const nextForm = leave
      ? {
          employeeId: leave.employeeId || "",
          type: leave.type || "annual",
          status: leave.status || "pending",
          startDate: normalizeDateInput(leave.startDate),
          endDate: normalizeDateInput(leave.endDate),
          notes: leave.notes || "",
        }
      : { ...INITIAL_FORM, status: "pending" };
    const fallbackId = lockedEmployeeId || employees[0]?.id || employees[0]?.value;
    if (!isAdmin) {
      nextForm.employeeId = lockedEmployeeId || leave?.employeeId || fallbackId || "";
      nextForm.status = leave?.status || "pending";
    } else if (!nextForm.employeeId && fallbackId) {
      nextForm.employeeId = fallbackId;
    }
    setForm(nextForm);
    setLocalError("");
  }, [leave, open, isAdmin, employees, lockedEmployeeId]);

  const employeeOptions = useMemo(
    () =>
      employees.map((emp) => ({
        value: emp.id,
        label: emp.name || emp.email || "Unnamed employee",
      })),
    [employees]
  );

  const handleChange = (field) => (event) => {
    const value = event?.target?.value ?? "";
    let nextError = "";
    setForm((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "startDate" && prev.endDate && value && new Date(value) > new Date(prev.endDate)) {
        next.endDate = value;
      }
      if (field === "endDate" && prev.startDate && value && new Date(value) < new Date(prev.startDate)) {
        nextError = "End date cannot be before start date.";
        next.endDate = prev.startDate;
      }

      return next;
    });
    setLocalError(nextError);
  };

  const validate = () => {
    if (!form.employeeId) {
      setLocalError("Employee is required.");
      return false;
    }
    if (!form.startDate || !form.endDate) {
      setLocalError("Start and end dates are required.");
      return false;
    }
    if (new Date(form.startDate) > new Date(form.endDate)) {
      setLocalError("End date cannot be before start date.");
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (isReadOnlyStatus) return;
    if (!validate()) return;
    onSubmit?.({
      ...form,
    });
  };

  if (!open) return null;

  const durationDays =
    form.startDate && form.endDate
      ? Math.max(1, Math.round((new Date(form.endDate) - new Date(form.startDate)) / (1000 * 60 * 60 * 24)) + 1)
      : null;

  return (
    <div className="modal-overlay solo-overlay" role="dialog" aria-modal="true" onClick={(event) => {
      if (event.target === event.currentTarget) {
        onClose?.();
      }
    }}>
      <div className="create-layer solo-drawer">
        <aside className="create-drawer" ref={drawerRef} aria-label="Leave drawer">
          {localError || error ? (
            <div className="inline-alert error">
              <span>{localError || error}</span>
            </div>
          ) : null}
          <div className="drawer-section">
            <h3>{isEditMode ? "Edit leave" : "New leave"}</h3>
            <div className="drawer-grid">
              <label className="field">
                <span className="field-label">Employee</span>
                <select value={form.employeeId} onChange={handleChange("employeeId")} disabled={!isAdmin || isEditMode}>
                  <option value="">Select employee</option>
                  {employeeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span className="field-label">Type</span>
                <select value={form.type} onChange={handleChange("type")} disabled={isReadOnlyStatus}>
                  <option value="annual">Annual vacation</option>
                  <option value="sick">Sick leave</option>
                </select>
              </label>
              <label className="field">
                <span className="field-label">Status</span>
                <select value={form.status} onChange={handleChange("status")} disabled={!isAdmin}>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="denied">Denied</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </label>
              <label className="field">
                <span className="field-label">Start date</span>
                <input
                  type="date"
                  value={form.startDate}
                  onChange={handleChange("startDate")}
                  disabled={isReadOnlyStatus}
                />
              </label>
              <label className="field">
                <span className="field-label">End date</span>
                <input
                  type="date"
                  value={form.endDate}
                  onChange={handleChange("endDate")}
                  min={form.startDate || undefined}
                  disabled={isReadOnlyStatus}
                />
              </label>
              {durationDays ? (
                <div className="field">
                  <span className="field-label">Duration</span>
                  <div className="muted">{durationDays} day{durationDays === 1 ? "" : "s"}</div>
                </div>
              ) : null}
            </div>
          </div>
          <div className="drawer-section">
            <div className="drawer-title-group">
              <div className="field-label">Notes</div>
              <div className="muted extra-small">Optional context for the approval.</div>
            </div>
            <textarea
              rows={4}
              className="notes-textarea"
              value={form.notes}
              onChange={handleChange("notes")}
              placeholder="Reason, travel dates, doctor note reference, etc."
              disabled={isReadOnlyStatus}
            />
          </div>
          <footer className="drawer-footer">
            {!isReadOnlyStatus ? (
              <button className="primary-btn wide" type="button" onClick={handleSubmit} disabled={saving}>
                {saving ? "Saving…" : isEditMode ? "Save changes" : "Save leave"}
              </button>
            ) : (
              <div className="muted extra-small">Approved or rejected requests cannot be edited.</div>
            )}
            <button className="ghost-btn subtle wide" type="button" onClick={onClose} disabled={saving}>
              {isReadOnlyStatus ? "Close" : "Cancel"}
            </button>
          </footer>
        </aside>
      </div>
    </div>
  );
}

export default LeaveDrawer;
